import { api } from "../utils/apiClient";

export const getConnection = () => api.get("/google-analytics/connection");

export const startConnect = () => api.get("/google-analytics/connect");

export const completeConnect = (code, state) =>
  api.post("/google-analytics/callback", { code, state });

export const getProperties = (refresh = false) =>
  api.get(`/google-analytics/properties${refresh ? "?refresh=true" : ""}`);

export const setDefaultProperty = (propertyId) =>
  api.patch("/google-analytics/property", { property_id: propertyId });

export const setPropertyLabel = (propertyId, label) =>
  api.patch("/google-analytics/property-label", { property_id: propertyId, label });

/**
 * Fetch the dashboard for a property over either a named preset or an explicit date pair.
 *
 * The presets are deliberately NOT resolved to dates here. Every request does end up as a concrete
 * start/end pair, but the resolution belongs on the server for three reasons:
 *
 * 1. Each window carries an equal-length preceding period that the KPI deltas compare against. The
 *    client never sends those, so sending explicit dates would not make the request self-contained
 *    anyway — the server still derives half the window either way.
 * 2. A preset window is anchored to the server clock and ends _yesterday_ (today is partial and would
 *    drag every delta negative). Resolving "7 days" against the browser clock would hand a user in
 *    UTC-8 a different window than one in UTC+5:30 for the same click, and fragment the server-side
 *    report cache — which is keyed on property + dates — into a separate entry per timezone.
 * 3. The preset is also UI state (which pill is lit). Keeping one representation avoids the UI holding
 *    "7d" while the request holds dates that no longer map back to a pill.
 *
 * An explicit pair overrides the preset server-side; send one or the other, never both.
 */
export const getDashboard = ({ propertyId, range, startDate, endDate } = {}) => {
  const params = new URLSearchParams();
  if (propertyId) params.set("property_id", propertyId);
  if (startDate && endDate) {
    params.set("start_date", startDate);
    params.set("end_date", endDate);
  } else if (range) {
    params.set("range", range);
  }
  const query = params.toString();
  return api.get(`/google-analytics/dashboard${query ? `?${query}` : ""}`);
};

export const disconnect = () => api.delete("/google-analytics/connection");
